import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

/**
 * Apollo Cache Manager - Conditional Cache Purging
 *
 * This utility monitors patient count changes and purges the Apollo cache
 * when appropriate conditions are met to prevent memory leaks.
 *
 * Key triggers for cache purging:
 * 1. Patient count drops from non-zero to zero (end of day scenario)
 * 2. Time-based purging (every few hours during operation)
 */

interface PurgeThresholds {
  zeroPatients: boolean;
  timeBased: number; // milliseconds, 0 to disable
  minTimeBetweenPurges: number; // milliseconds
}

interface PurgeDecision {
  trigger: boolean;
  reason: string;
  priority?: 'high' | 'low';
}

interface CacheStats {
  totalObjects: number;
  patientObjects: number;
  lastPurgeTime: number;
  timeSinceLastPurge: number;
}

interface RoomData {
  waitingRoom?: { patients?: any[] };
  preOp?: { patients?: any[] };
  pacu?: { patients?: any[] };
  postOps?: Array<{ patients?: any[] }>;
  operationRooms?: Array<{ patients?: any[] }>;
}

interface PurgeParams {
  totalCurrentPatients: number;
  previousTotal: number;
  timeSinceLastPurge: number;
}

class CacheManager {
  private client: ApolloClient<NormalizedCacheObject>;
  private previousTotal: number;
  private lastPurgeTime: number;
  private purgeThresholds: PurgeThresholds;

  constructor(apolloClient: ApolloClient<NormalizedCacheObject>) {
    this.client = apolloClient;
    this.previousTotal = 0;
    this.lastPurgeTime = Date.now();
    this.purgeThresholds = {
      // Purge when patient count drops to zero from non-zero
      // Temporarily disabled
      zeroPatients: false,
      // Time-based purging in milliseconds (set to 0 to disable)
      // Temporarily disabled
      timeBased: 0, // 30 minutes
      // Minimum time between purges in milliseconds (prevent too frequent purging)
      minTimeBetweenPurges: 30 * 1000, // 30 seconds
    };
  }

  /**
   * Monitor patient counts and trigger cache purging when appropriate
   */
  monitorAndPurge(roomData: RoomData): void {
    const currentTime = Date.now();
    const timeSinceLastPurge = currentTime - this.lastPurgeTime;

    const totalCurrentPatients = this.calculateTotalPatients(roomData);

    const shouldPurge = this.shouldPurgeCache({
      totalCurrentPatients,
      previousTotal: this.previousTotal,
      timeSinceLastPurge,
    });

    if (shouldPurge.trigger) {
      this.purgeCache(shouldPurge.reason);
      this.lastPurgeTime = currentTime;
    }

    this.previousTotal = totalCurrentPatients;
  }

  /**
   * Calculate total patient count across all rooms
   */
  private calculateTotalPatients(roomData: RoomData): number {
    let total = 0;

    if (roomData.waitingRoom?.patients) {
      total += roomData.waitingRoom.patients.length;
    }

    if (roomData.preOp?.patients) {
      total += roomData.preOp.patients.length;
    }

    if (roomData.pacu?.patients) {
      total += roomData.pacu.patients.length;
    }

    if (roomData.postOps && Array.isArray(roomData.postOps)) {
      total += roomData.postOps.reduce((sum, room) => sum + (room?.patients?.length || 0), 0);
    }

    if (roomData.operationRooms && Array.isArray(roomData.operationRooms)) {
      total += roomData.operationRooms.reduce((sum, room) => sum + (room?.patients?.length || 0), 0);
    }

    return total;
  }

  /**
   * Determine if cache should be purged based on conditions
   */
  private shouldPurgeCache({ totalCurrentPatients, previousTotal, timeSinceLastPurge }: PurgeParams): PurgeDecision {
    // Prevent too frequent purging
    if (timeSinceLastPurge < this.purgeThresholds.minTimeBetweenPurges) {
      return { trigger: false, reason: 'too_recent' };
    }

    // Patient count dropped to zero from non-zero (end of day)
    if (this.purgeThresholds.zeroPatients && totalCurrentPatients === 0 && previousTotal > 0) {
      return {
        trigger: true,
        reason: `zero_patients_from_${previousTotal}`,
        priority: 'high',
      };
    }

    if (this.purgeThresholds.timeBased > 0 && timeSinceLastPurge >= this.purgeThresholds.timeBased) {
      console.log('✅ Time-based purge triggered!');
      return {
        trigger: true,
        reason: `time_based_${Math.round(timeSinceLastPurge / (60 * 60 * 1000))}h`,
        priority: 'low',
      };
    }

    return { trigger: false, reason: 'no_conditions_met' };
  }

  /**
   * Purge Apollo cache with logging
   */
  private purgeCache(reason: string): void {
    try {
      console.log(`🧹 Apollo Cache Purge triggered: ${reason}`);

      // Perform garbage collection first
      this.client.cache.gc();

      // For more aggressive purging, we can also evict specific types
      // that are known to accumulate data (like Patient objects)
      this.evictPatientData();

      console.log(`✅ Apollo Cache purged successfully (reason: ${reason})`);
    } catch (error) {
      console.error('❌ Error purging Apollo cache:', error);
    }
  }

  /**
   * Evict patient-related data from cache
   * This is more targeted than a full cache reset
   */
  private evictPatientData(): void {
    try {
      // Evict all Patient objects from cache
      // This will remove accumulated caretakerMessages, logs, vitals, etc.
      this.client.cache.evict({
        fieldName: 'patients',
        broadcast: false, // Don't trigger re-renders during cleanup
      });

      // Also evict individual patient objects if we can identify them
      // This is more aggressive cleanup for patient-specific data
      const cacheData = this.client.cache.extract();
      Object.keys(cacheData).forEach(key => {
        if (key.startsWith('Patient:')) {
          this.client.cache.evict({
            id: key,
            broadcast: false,
          });
        }
      });
    } catch (error) {
      console.error('Error evicting patient data:', error);
    }
  }

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats(): CacheStats | null {
    try {
      const cacheData = this.client.cache.extract();
      const totalObjects = Object.keys(cacheData).length;
      const patientObjects = Object.keys(cacheData).filter(key => key.startsWith('Patient:')).length;

      return {
        totalObjects,
        patientObjects,
        lastPurgeTime: this.lastPurgeTime,
        timeSinceLastPurge: Date.now() - this.lastPurgeTime,
      };
    } catch (error) {
      console.error('Error getting cache stats:', error);
      return null;
    }
  }
}

export default CacheManager;
